///////////////////////////////////
// IMPORTS
///////////////////////////////////
// schema
import { LoginStatus } from "@clockwise/schema";
// libraries
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ApolloError, useMutation } from "@apollo/client";
import { withRouter } from "found";
// component
import { IProps, IContainer } from "./MsftLoginButton.d";
import { styles } from "./MsftLoginButton.styles";
// util
import {
  getMicrosoftAuthUrl,
  IM365AuthCompleteMessageData,
  M365_AUTH_COMPLETE_MESSAGE,
} from "@util/msft-auth.util";
import { doUserLogin } from "@util/auth.util";
import { getServerUrl } from "@clockwise/web-commons/src/util/post-message-common.util";
import { logger } from "@util/logger.util";
// constants
import { paths } from "@clockwise/client-commons/src/constants/site";
// state
import { IReduxState } from "@state/reducers/root.reducer";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { resetOnboardingState } from "@state/local-storage";
// assets
import { msftLogo } from "@img/microsoft";
// mutations
import {
  LoginMicrosoftUserDocument,
  LoginMicrosoftUserMutation,
} from "./__generated__/LoginMicrosoftUser.generated";
// material-ui
import { Button, CircularProgress } from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";

///////////////////////////////////
// COMPONENT
///////////////////////////////////
const MsftLoginButtonCmpt = (props: IProps) => {
  // let popup: Window | null = null;
  const mountTime = Date.now();

  const [isConnecting, setIsConnecting] = useState<boolean>(false);

  const dispatch = useDispatch();
  const isChromeExtension = useSelector((state: IReduxState) => state.webExtension.isWebExtension);
  const authed = useSelector((state: IReduxState) => state.auth.authed);

  console.log(`isChromeExtension: ${isChromeExtension}`);
  console.log(`authed: ${authed}`);

  // if (authed && !props.preventLoginRedirect) {
  //   if (isChromeExtension) {
  //     props.router.replace("/chrome.html");
  //   } else if (props.redirect) {
  //     props.router.replace(`/${props.redirect}`);
  //   } else {
  //     props.router.replace("/app");
  //   }
  // }

  // ~-~-~-~-~-~-~-
  // Auth
  // ~-~-~-~-~-~-~-
  const [loginMicrosoftUser] = useMutation(LoginMicrosoftUserDocument, {
    onCompleted: (data: LoginMicrosoftUserMutation) => {
      processMicrosoftLoginMutationPayload(data);
    },
    onError: (error: ApolloError) => {
      logger.error("loginMicrosoftUser mutation failed", error);
      setIsConnecting(false);
    },
  });

  const processMicrosoftLoginMutationPayload = (data: LoginMicrosoftUserMutation) => {
    const payload = data.loginMicrosoftUser;

    if (
      !payload ||
      payload.verifyCodeMessage === null ||
      payload.verifyCodeResult === null ||
      !payload.viewer?.user
    ) {
      logger.error("Incomplete fragment for loginMicrosoftUser");
      return;
    }

    if (payload.viewer.user.createdTime >= mountTime) {
      resetOnboardingState();
    }

    setIsConnecting(false);
    let badLoginStatusMessage = "";
    switch (payload.loginStatus) {
      // success!
      case LoginStatus.Success:
        doUserLogin(payload.viewer, payload.operation, payload.orgOperation);
        handleSuccess(payload.verifyCodeResult, payload.verifyCodeMessage);
        if (!props.hideSnackBarMessages) {
          dispatch(snackBarOpen("Welcome to Clockwise!"));
        }
        break;
      // successful user login, but bad status
      case LoginStatus.GoogleContactSharingDisabled:
        badLoginStatusMessage = "Please update Google to allow Clockwise to access your contacts.";
        handleSuccess(payload.verifyCodeResult, payload.verifyCodeMessage);
        break;
      // successful user login, but bad status
      case LoginStatus.GoogleApiAccessDisabled:
        badLoginStatusMessage = "Please update Google to allow Clockwise to have API access.";
        handleSuccess(payload.verifyCodeResult, payload.verifyCodeMessage);
        break;
      // generic failure of onboarding the org, has a user, but bad status
      case LoginStatus.NoCrawl:
        badLoginStatusMessage = "Sorry, there was a problem onboarding your account.";
        handleSuccess(payload.verifyCodeResult, payload.verifyCodeMessage);
        break;
      // no user, bad login status
      case LoginStatus.PersonalEmail:
        badLoginStatusMessage =
          "Please sign in with your work Google account. " +
          "Clockwise does not currently support signing in with a personal Google account.";
        handleFailure();
        break;
      // no user, bad login status
      case LoginStatus.WaitlistedEmail:
        badLoginStatusMessage =
          "Thanks for your interest in Clockwise. We are working hard " +
          "to increase access to our service. We have added you to our waitlist and will be in touch soon.";
        handleFailure();
        break;
      case LoginStatus.SecondaryDomainUsed:
        badLoginStatusMessage =
          "It appears you are using a secondary domain for your " +
          "organization. Please sign in using your Google account for your primary domain.";
        handleFailure();
        break;
      case LoginStatus.Deactivating:
        badLoginStatusMessage =
          "You recently deactivated your Clockwise account. While we process " +
          "your deactivation it is not possible to sign in. Apologies for the inconvenience, please try again later.";
        handleFailure();
        break;
      case LoginStatus.BlockedByAdmin:
        badLoginStatusMessage =
          "Your admin removed your access to Clockwise. To remove Clockwise from your calendar, " +
          "please delete the Chrome extension. If this was a mistake, contact your admins. ";
        handleFailure();
        break;
      // meh
      default:
        handleFailure("Missing login status, sorry something went wrong.");
        break;
    }

    // if we have a bad login status, display it for 20 seconds
    if (badLoginStatusMessage && !props.hideSnackBarMessages) {
      dispatch(
        snackBarOpen(
          badLoginStatusMessage,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          20000,
        ),
      );
    }
  };

  // ~-~-~-~-~-~-~-
  // Callbacks
  // ~-~-~-~-~-~-~-
  const handleSuccess = (verifyCodeResult: boolean, verifyCodeMessage: string) => {
    if (props.onSuccess) {
      props.onSuccess(verifyCodeResult, verifyCodeMessage);
    }
  };

  const handleFailure = (error?: string) => {
    if (props.onFailure) {
      props.onFailure(error);
    }
  };

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!!props.onClick) {
      props.onClick(e);
    }

    setIsConnecting(true);
    openAuthPopup();
  };

  const onAuthCallback = (event: MessageEvent<IM365AuthCompleteMessageData>) => {
    if (event.origin !== getServerUrl() || event.data.message !== M365_AUTH_COMPLETE_MESSAGE) {
      return;
    }

    if (props.onStart) {
      props.onStart();
    }

    if (!event.data.success || !event.data.authCode) {
      if (props.onMicrosoftFailure) {
        props.onMicrosoftFailure();
      }
      return;
    }

    loginMicrosoftUser({
      variables: {
        microsoftAuthCode: event.data.authCode,
        redirectUri: `${getServerUrl()}${paths.microsoftAuthRedirect}`,
      },
    });
  };

  const openAuthPopup = () => {
    window.open(getMicrosoftAuthUrl(), "_blank", "width=650,height=780");

    window.removeEventListener("message", onAuthCallback);
    window.addEventListener("message", onAuthCallback, { once: true });
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  return (
    <Button
      variant="contained"
      startIcon={
        isConnecting ? (
          <CircularProgress variant="indeterminate" size={20} />
        ) : (
          <img className={props.classes.logo} src={msftLogo} alt="microsoft-logo" />
        )
      }
      disabled={isConnecting}
      onClick={onClick}
      classes={{ root: props.classes.button, startIcon: props.classes.startIcon }}
      fullWidth
    >
      <span className={props.classes.label}>{isConnecting ? "Connecting" : "Office 365"}</span>
    </Button>
  );
};

/////////////////
// With Styles
/////////////////
const MsftLoginButtonStyled = withStyles(styles)(MsftLoginButtonCmpt);

export const MsftLoginButton = withRouter(
  MsftLoginButtonStyled,
) as React.ComponentClass<IContainer>;
